import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SessionService } from "@app/shared/services/session.service";
import { DashboardsService } from '@src/app/shared/backend/clients/api/dashboards.service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { MenuService } from '@src/app/shared/services/menu.service';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { Subject} from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';


@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
    host: {
        class: 'app-dashboard'
    },
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {

  public dashboardRead: boolean = false;
  public dashboardTotalCount = 0;

  private destroyDashboard$ = new Subject<void>();
  private dashboardsLoaded:boolean = false;
  public dashboards: any[] = [];
  public dashboardTotalRecords: number = 0;
  public dashboardRowsPerPageOption: number = 10;
  public dashboardFormGroup: FormGroup;

  public placeholder: string = '';
  public selectedDashboardUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    public session: SessionService,
    private permissionService: PermissionService,
    private menuService: MenuService,
    private dashboardsService: DashboardsService
  ) {
    this.dashboardFormGroup = this.fb.group({
      _dashboard: [[]]
    });
  }

  async ngOnInit() {
    this.dashboardFormGroup.get('_dashboard')!.valueChanges.pipe(
      map((value: any) => {
        return value?.[0] ?? null
      }),
      filter((id) => {
        return !!id
      }),
      distinctUntilChanged(),
      switchMap((id) => this.dashboardsService.apiDashboardsMetabaseIdGet(id)),
      takeUntil(this.destroyDashboard$)
    ).subscribe({
      next: (res: any) => this.selectedDashboardUrl = res.url,
      error: (err: any) => console.error('Error loading dashboard embed URL:', err)
    });

    this.dashboardRead = await this.permissionService.hasActionPermissions(Actions.view_dashboards);
    const menus = await this.menuService.getUserMenus();

    if (this.dashboardRead && menus?.some(x => x.feature === Features.Dashboards)) {
      this.loadDashboardsOnce();
    }
  }

  private loadDashboardsOnce(): void {
    if (this.dashboardsLoaded) return;
    this.dashboardsLoaded = true;

    this.dashboardsService.apiDashboardsGet().pipe(take(1)).subscribe({
      next: (dashboardsResponse: any) => {
        this.dashboards = Array.isArray(dashboardsResponse.results) ? dashboardsResponse.results : [];
        this.dashboardTotalCount = dashboardsResponse.totalCount;

        if (!this.dashboards.length) {
          console.error('No dashboards available.');
          return;
        }

        // Find the dashboard with the lowest priority
        const defaultDashboard = this.dashboards.reduce(
          (min, cur) => (cur.priority < min.priority ? cur : min),
          this.dashboards[0]
        );

        this.placeholder = defaultDashboard.name;

        // Set the default dashboard
        this.dashboardFormGroup.get('_dashboard')?.setValue([defaultDashboard.metabaseId]);
      },
      error: (err: any) => {
        console.error('Error fetching dashboards:', err);
        this.dashboards = [];
      }
    });
  }

  ngOnDestroy() {
    this.destroyDashboard$.next();
    this.destroyDashboard$.complete();
  }
}
